import { getPartyPassSettingsUpgradePath } from "./party-pass-upsell";
import { isPaidParty, isPartyPlan } from "./party-entitlements";
import type { PartyPlan, PartyUnlockSource } from "./types/parties-api";

export const TV_MODE_OPENED_EVENT = "tv_mode_opened";
export const TV_MODE_UPSELL_CLICKED_EVENT = "tv_mode_upsell_clicked";

/** URL-safe length. 32 characters is not practical to guess. */
export const TV_DISPLAY_TOKEN_LENGTH = 32;

/**
 * Landscape screens at least this wide relative to their height put the
 * podium and the standings side by side. 6/5 matches the TV stage media query.
 */
export const TV_LAYOUT_SPLIT_ASPECT = 1.2;
/** Hide the TV full-screen control after this long with no pointer or key activity. */
export const TV_FULLSCREEN_IDLE_MS = 4000;
export const TV_LAYOUT_THEATER_SHORT_SIDE = 1440;
export const TV_LAYOUT_LIVING_ROOM_SHORT_SIDE = 900;

const TV_DISPLAY_TOKEN_PATTERN = /^[A-Za-z0-9_-]+$/;

export type TvModeAccess = "allowed" | "upgrade_required";
export type TvOrientation = "landscape" | "portrait";
export type TvLayoutScale = "theater" | "living-room" | "laptop";
export type TvModeSurface = "host" | "display";

export interface TvLayout {
  orientation: TvOrientation;
  scale: TvLayoutScale;
  split: boolean;
}

export interface TvModeAccessInput {
  plan?: PartyPlan | string | null;
  unlockSource?: PartyUnlockSource | string | null;
}

export interface TvStandingSource {
  userId: string;
  username: string | null;
  name: string | null;
  email?: string | null;
  imageUrl: string | null;
  rank: number;
  correctCount: number;
  totalCategories: number;
}

export interface TvStanding {
  rank: number;
  displayName: string;
  imageUrl: string | null;
  correctCount: number;
  totalCategories: number;
}

export interface TvFeaturedWinner {
  categoryName: string;
  winnerName: string;
  imageUrl: string | null;
}

/**
 * Optional company mark for a branded party. The TV screen renders this
 * on the same stage. It stays empty until party branding is available.
 */
export interface TvDisplayBranding {
  displayName: string;
  logoUrl: string | null;
  iconUrl: string | null;
  primaryColor: string;
  accentColor: string;
  showPoweredBy: boolean;
}

export interface TvDisplayLocked {
  access: "upgrade_required";
  partyId: string;
  partyName: string;
  showName: string;
  showYear: number;
  upgradePath: string;
}

export interface TvDisplayLive {
  access: "allowed";
  partyId: string;
  partyName: string;
  showName: string;
  showYear: number;
  /** ISO start time when the award show has one. Null before it is known. */
  showStartsAt: string | null;
  joinUrl: string;
  announcedCount: number;
  totalCategories: number;
  featured: TvFeaturedWinner | null;
  standings: TvStanding[];
  updatedAt: string;
  /** Null when the party has no branding, including before that feature exists. */
  branding: TvDisplayBranding | null;
}

export type TvDisplaySnapshot = TvDisplayLocked | TvDisplayLive;

export interface BuildTvDisplaySnapshotInput {
  plan: PartyPlan | string | null | undefined;
  partyId: string;
  partyName: string;
  showName: string;
  showYear: number;
  showStartsAt?: string | null;
  joinUrl: string;
  announcedCount: number;
  totalCategories: number;
  featured: TvFeaturedWinner | null;
  standings: readonly TvStandingSource[];
  guestLabel: string;
  now: Date;
  branding?: TvDisplayBranding | null;
}

/**
 * Party Pass is the gate. Admin, store, and Stripe unlocks all set
 * `plan` to `party_pass`, so they are included. A stray unlock source
 * on a free party does not open TV mode.
 */
export function resolveTvModeAccess(
  input: PartyPlan | string | null | undefined | TvModeAccessInput,
): TvModeAccess {
  const plan =
    typeof input === "object" && input !== null ? input.plan : input;
  return isPaidParty(plan) ? "allowed" : "upgrade_required";
}

/** The corner control is on until the viewer goes idle, and stays off in full screen. */
export function isTvFullscreenControlVisible(input: {
  fullscreen: boolean;
  idle: boolean;
}): boolean {
  return !input.fullscreen && !input.idle;
}

/** Join QR stays up until a category is called or someone has points. */
export function isTvPreShow(input: {
  announcedCount: number;
  standings: readonly { correctCount: number }[];
}): boolean {
  if (input.announcedCount > 0) {
    return false;
  }
  return input.standings.every((standing) => standing.correctCount <= 0);
}

function whole(value: number, fallback: number): number {
  if (!Number.isFinite(value)) {
    return fallback;
  }
  return Math.floor(value);
}

/**
 * Room-readable join label. Public hosts keep host and path without a
 * protocol. Loopback hosts are not useful on a TV, so only the party code shows.
 */
export function formatTvJoinDisplay(joinUrl: string): string {
  const trimmed = joinUrl.trim();
  if (!trimmed) {
    return "";
  }
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return trimmed;
  }
  const host = url.hostname.toLowerCase().replace(/^\[|\]$/g, "");
  const segments = url.pathname.split("/").filter(Boolean);
  const code = segments.at(-1) ?? "";
  const local =
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "::1" ||
    host.endsWith(".local");
  if (local) {
    return code;
  }
  return segments.length > 0 ? `${url.host}/${segments.join("/")}` : url.host;
}

/**
 * One column when the list fits. Extra columns when it does not.
 * The last cell becomes "+N more" once every column is full.
 */
export function layoutTvGuests(input: {
  count: number;
  maxRows: number;
  maxColumns: number;
}): { columns: number; shown: number; extra: number } {
  const count = Math.max(0, whole(input.count, 0));
  const maxRows = Math.max(1, whole(input.maxRows, 1));
  const maxColumns = Math.max(1, whole(input.maxColumns, 1));
  const columns = Math.min(
    maxColumns,
    Math.max(1, Math.ceil(count / maxRows)),
  );
  const capacity = maxRows * columns;
  if (count <= capacity) {
    return { columns, shown: count, extra: 0 };
  }
  const shown = Math.max(0, capacity - 1);
  return { columns, shown, extra: count - shown };
}

export function resolveTvLayout(width: number, height: number): TvLayout {
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return { orientation: "landscape", scale: "living-room", split: true };
  }

  const orientation: TvOrientation = height > width ? "portrait" : "landscape";
  const shortSide = Math.min(width, height);
  const scale: TvLayoutScale =
    shortSide >= TV_LAYOUT_THEATER_SHORT_SIDE
      ? "theater"
      : shortSide >= TV_LAYOUT_LIVING_ROOM_SHORT_SIDE
        ? "living-room"
        : "laptop";
  const split =
    orientation === "landscape" && width / height >= TV_LAYOUT_SPLIT_ASPECT;

  return { orientation, scale, split };
}

export interface TvBox {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** Largest whole-pixel size in range whose `fits` check passes. */
export function chooseTvFontSize({
  minPx,
  maxPx,
  fits,
}: {
  minPx: number;
  maxPx: number;
  fits: (px: number) => boolean;
}): number {
  const min = Math.max(1, Math.floor(minPx));
  const max = Math.max(min, Math.floor(maxPx));
  if (fits(max)) {
    return max;
  }
  if (!fits(min)) {
    return min;
  }
  let lo = min;
  let hi = max;
  let best = min;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (fits(mid)) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return best;
}

/** True when the boxes share interior area. Edge contact is not an overlap. */
export function tvBoxesOverlap(a: TvBox, b: TvBox, slack = 0.5): boolean {
  const width = Math.min(a.right, b.right) - Math.max(a.left, b.left);
  const height = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  return width > slack && height > slack;
}

export function isTvDisplayToken(value: string): boolean {
  return (
    value.length === TV_DISPLAY_TOKEN_LENGTH &&
    TV_DISPLAY_TOKEN_PATTERN.test(value)
  );
}

export function tvDisplayPath(token: string): string {
  return `/tv/${encodeURIComponent(token)}`;
}

function presentName(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? "";
  if (!trimmed || trimmed.includes("@")) {
    return null;
  }
  return trimmed;
}

/** Ranks that more than one person shares. Unique places stay as plain numbers. */
export function tiedTvRanks(
  standings: readonly { rank: number }[],
): ReadonlySet<number> {
  const counts = new Map<number, number>();
  for (const standing of standings) {
    counts.set(standing.rank, (counts.get(standing.rank) ?? 0) + 1);
  }
  const tied = new Set<number>();
  for (const [rank, count] of counts) {
    if (count > 1) {
      tied.add(rank);
    }
  }
  return tied;
}

export function toTvStandings(
  entries: readonly TvStandingSource[],
  guestLabel: string,
): TvStanding[] {
  return [...entries]
    .sort((left, right) => left.rank - right.rank)
    .map((entry) => ({
      rank: entry.rank,
      displayName:
        presentName(entry.username) ??
        presentName(entry.name) ??
        guestLabel,
      imageUrl: entry.imageUrl,
      correctCount: entry.correctCount,
      totalCategories: entry.totalCategories,
    }));
}

export function buildTvDisplaySnapshot(
  input: BuildTvDisplaySnapshotInput,
): TvDisplaySnapshot {
  if (resolveTvModeAccess(input.plan) !== "allowed") {
    return {
      access: "upgrade_required",
      partyId: input.partyId,
      partyName: input.partyName,
      showName: input.showName,
      showYear: input.showYear,
      upgradePath: getPartyPassSettingsUpgradePath(input.partyId),
    };
  }

  return {
    access: "allowed",
    partyId: input.partyId,
    partyName: input.partyName,
    showName: input.showName,
    showYear: input.showYear,
    showStartsAt: input.showStartsAt?.trim() ? input.showStartsAt : null,
    joinUrl: input.joinUrl,
    announcedCount: input.announcedCount,
    totalCategories: input.totalCategories,
    featured: input.featured,
    standings: toTvStandings(input.standings, input.guestLabel),
    updatedAt: input.now.toISOString(),
    branding: normalizeTvDisplayBranding(input.branding),
  };
}

const TV_BRAND_NAME_MAX = 80;
const TV_BRAND_HEX = /^#[0-9A-Fa-f]{6}$/;

function isTvBrandAsset(value: unknown): value is string | null {
  if (value === null) {
    return true;
  }
  if (typeof value !== "string") {
    return false;
  }
  const trimmed = value.trim();
  if (!trimmed || /\s/.test(trimmed)) {
    return false;
  }
  return trimmed.startsWith("/") || trimmed.startsWith("https://");
}

/** Active party branding for the TV stage. Disabled or unsafe marks stay off the screen. */
export function tvDisplayBrandingFromParty(
  branding: {
    enabled?: boolean;
    displayName?: string | null;
    logoUrl?: string | null;
    iconUrl?: string | null;
    primaryColor?: string | null;
    accentColor?: string | null;
    showPoweredBy?: boolean | null;
  } | null | undefined,
): TvDisplayBranding | null {
  if (!branding?.enabled) {
    return null;
  }
  return normalizeTvDisplayBranding({
    displayName: branding.displayName,
    logoUrl: branding.logoUrl ?? null,
    iconUrl: branding.iconUrl ?? null,
    primaryColor: branding.primaryColor,
    accentColor: branding.accentColor,
    showPoweredBy: branding.showPoweredBy,
  });
}

/** Keep a brand mark only when its name, colors, and assets are safe to render. */
export function normalizeTvDisplayBranding(
  value: unknown,
): TvDisplayBranding | null {
  if (value == null) {
    return null;
  }
  if (!isRecord(value)) {
    return null;
  }
  const displayName =
    typeof value.displayName === "string" ? value.displayName.trim() : "";
  if (!displayName || displayName.length > TV_BRAND_NAME_MAX) {
    return null;
  }
  if (
    typeof value.primaryColor !== "string" ||
    !TV_BRAND_HEX.test(value.primaryColor) ||
    typeof value.accentColor !== "string" ||
    !TV_BRAND_HEX.test(value.accentColor)
  ) {
    return null;
  }
  if (!isTvBrandAsset(value.logoUrl) || !isTvBrandAsset(value.iconUrl)) {
    return null;
  }
  if (typeof value.showPoweredBy !== "boolean") {
    return null;
  }
  return {
    displayName,
    logoUrl: typeof value.logoUrl === "string" ? value.logoUrl.trim() : null,
    iconUrl: typeof value.iconUrl === "string" ? value.iconUrl.trim() : null,
    primaryColor: value.primaryColor.toUpperCase(),
    accentColor: value.accentColor.toUpperCase(),
    showPoweredBy: value.showPoweredBy,
  };
}

/** Black or white text that stays readable on a brand primary color. */
export function tvBrandOnPrimary(hex: string): "#000000" | "#FFFFFF" {
  const normalized = /^#([0-9A-Fa-f]{6})$/.exec(hex.trim());
  if (!normalized) {
    return "#FFFFFF";
  }
  const raw = normalized[1] ?? "";
  const channel = (index: number) => {
    const value = parseInt(raw.slice(index * 2, index * 2 + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  const luminance =
    0.2126 * channel(0) + 0.7152 * channel(1) + 0.0722 * channel(2);
  const contrast = (foreground: number) => {
    const lighter = Math.max(foreground, luminance);
    const darker = Math.min(foreground, luminance);
    return (lighter + 0.05) / (darker + 0.05);
  };
  return contrast(0) >= contrast(1) ? "#000000" : "#FFFFFF";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function parseShowStartsAt(value: unknown): string | null | undefined {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  if (typeof value !== "string" || Number.isNaN(Date.parse(value))) {
    return undefined;
  }
  return value;
}

function parseFeatured(value: unknown): TvFeaturedWinner | null | undefined {
  if (value === null) {
    return null;
  }
  if (!isRecord(value)) {
    return undefined;
  }
  if (
    !isNonEmptyString(value.categoryName) ||
    !isNonEmptyString(value.winnerName)
  ) {
    return undefined;
  }
  if (value.imageUrl !== null && typeof value.imageUrl !== "string") {
    return undefined;
  }
  return {
    categoryName: value.categoryName,
    winnerName: value.winnerName,
    imageUrl: value.imageUrl,
  };
}

function parseStanding(value: unknown): TvStanding | null {
  if (!isRecord(value)) {
    return null;
  }
  if (
    !isFiniteNumber(value.rank) ||
    !isNonEmptyString(value.displayName) ||
    !isFiniteNumber(value.correctCount) ||
    !isFiniteNumber(value.totalCategories)
  ) {
    return null;
  }
  if (value.imageUrl !== null && typeof value.imageUrl !== "string") {
    return null;
  }
  return {
    rank: value.rank,
    displayName: value.displayName,
    imageUrl: value.imageUrl,
    correctCount: value.correctCount,
    totalCategories: value.totalCategories,
  };
}

export function parseTvDisplaySnapshot(value: unknown): TvDisplaySnapshot | null {
  if (!isRecord(value)) {
    return null;
  }

  if (value.access === "upgrade_required") {
    if (
      !isNonEmptyString(value.partyId) ||
      !isNonEmptyString(value.partyName) ||
      !isNonEmptyString(value.showName) ||
      !isFiniteNumber(value.showYear) ||
      !isNonEmptyString(value.upgradePath)
    ) {
      return null;
    }
    return {
      access: "upgrade_required",
      partyId: value.partyId,
      partyName: value.partyName,
      showName: value.showName,
      showYear: value.showYear,
      upgradePath: value.upgradePath,
    };
  }

  if (value.access !== "allowed") {
    return null;
  }

  const featured = parseFeatured(value.featured);
  const showStartsAt = parseShowStartsAt(value.showStartsAt);
  const branding =
    value.branding === undefined
      ? null
      : normalizeTvDisplayBranding(value.branding);
  if (value.branding != null && branding === null) {
    return null;
  }
  if (
    featured === undefined ||
    showStartsAt === undefined ||
    !isNonEmptyString(value.partyId) ||
    !isNonEmptyString(value.partyName) ||
    !isNonEmptyString(value.showName) ||
    !isFiniteNumber(value.showYear) ||
    typeof value.joinUrl !== "string" ||
    !isFiniteNumber(value.announcedCount) ||
    !isFiniteNumber(value.totalCategories) ||
    !isNonEmptyString(value.updatedAt) ||
    !Array.isArray(value.standings)
  ) {
    return null;
  }

  const standings: TvStanding[] = [];
  for (const entry of value.standings) {
    const standing = parseStanding(entry);
    if (!standing) {
      return null;
    }
    standings.push(standing);
  }

  return {
    access: "allowed",
    partyId: value.partyId,
    partyName: value.partyName,
    showName: value.showName,
    showYear: value.showYear,
    showStartsAt,
    joinUrl: value.joinUrl,
    announcedCount: value.announcedCount,
    totalCategories: value.totalCategories,
    featured,
    standings,
    updatedAt: value.updatedAt,
    branding,
  };
}

export function buildTvModeOpenedEventProperties(input: {
  partyId: string;
  plan?: PartyPlan | string | null;
  surface: TvModeSurface;
  access: TvModeAccess;
}): {
  party_id: string;
  surface: TvModeSurface;
  access: TvModeAccess;
  plan?: PartyPlan;
} {
  const plan = isPartyPlan(input.plan) ? input.plan : undefined;
  return {
    party_id: input.partyId.trim(),
    surface: input.surface,
    access: input.access,
    ...(plan ? { plan } : {}),
  };
}

export function buildTvModeUpsellClickedEventProperties(input: {
  partyId: string;
}): { party_id: string; plan: "free"; destination: string } {
  const partyId = input.partyId.trim();
  return {
    party_id: partyId,
    plan: "free",
    destination: getPartyPassSettingsUpgradePath(partyId),
  };
}
